import { useState, useEffect, useCallback } from 'react'

// The vessel's stored position (vessel_positions, read through /api/workspace - the login and vessel are checked there).
// The background collector (.github/workflows/ais-collect.yml) refreshes it hourly; while the app is open a collect
// also runs every 5 min. A vessel without an MMSI only has a position set by hand.
// Returns { position, status, refresh, setManual }. status: 'loading' | 'ok' | 'none' | 'error'
// position: { lat, lon, speed, course, name, ts, source: 'ais' | 'manual', place, set_by } (see src/lib/vesselPosition.js)
export function useVesselPosition(mmsi, vesselId) {
  const [position, setPosition] = useState(null)
  const [status, setStatus] = useState('loading')
  const key = mmsi ? String(mmsi) : vesselId ? 'vessel:' + vesselId : null
  const url = mmsi ? '/api/workspace?cached=1&mmsi=' + encodeURIComponent(mmsi) : '/api/workspace?cached=1&vessel=' + encodeURIComponent(vesselId || '')

  const read = useCallback(async () => {
    if (!key) { setStatus('none'); return }
    try {
      const res = await fetch(url)
      if (!res.ok) { setStatus(res.status >= 500 ? 'error' : 'none'); return }
      const p = ((await res.json()).positions || {})[key]
      if (p) { setPosition(p); setStatus('ok') } else setPosition(prev => { setStatus(prev ? 'ok' : 'none'); return prev })
    } catch (e) { setStatus(s => (s === 'ok' ? s : 'error')) }
  }, [key, url])

  useEffect(() => {
    if (!key) { setStatus('none'); return }
    let cancelled = false
    const poll = () => { if (!cancelled) read() }
    poll()
    const readId = setInterval(poll, 60000)
    // every 5 min while open: a collect for this vessel (silently updates the stored row, then re-read)
    const collectId = mmsi ? setInterval(async () => { try { await fetch('/api/workspace?mmsi=' + encodeURIComponent(mmsi)) } catch (e) {} poll() }, 5 * 60 * 1000) : null
    return () => { cancelled = true; clearInterval(readId); if (collectId) clearInterval(collectId) }
  }, [key, mmsi, read])

  // { port } or { latlon: '43.55, 7.02' } -> saved as source 'manual'; throws with the server's message
  const setManual = useCallback(async input => {
    const res = await fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'vessel_position_set', client_id: vesselId, ...input }) })
    const out = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(out.error || 'Could not save the position')
    if (out.position) { setPosition(out.position); setStatus('ok') }
    return out.position
  }, [vesselId])

  return { position, status, refresh: read, setManual }
}
